import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';

function BudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBudgets = useCallback(async (date) => {
    setIsLoading(true);
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const response = await api.get(`/budgets/?year=${year}&month=${month}`);
      setBudgets(response.data);
    } catch (error) {
      toast.error("Não foi possível carregar os orçamentos.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBudgets(selectedMonth);
  }, [selectedMonth, fetchBudgets]);

  const handleBudgetChange = (categoryId, amount) => {
    const newAmount = Math.max(0, parseFloat(amount) || 0);
    setBudgets(currentBudgets => currentBudgets.map(b => b.category_id === categoryId ? { ...b, budget_amount: newAmount } : b));
  };

  const handleSaveChanges = async () => {
    const loadingToast = toast.loading("Registrando orçamento base...");
    try {
      const promises = budgets.map(budget =>
        api.post('/budgets/', {
          category_id: budget.category_id,
          amount: budget.budget_amount,
          year: selectedMonth.getFullYear(),
          month: selectedMonth.getMonth() + 1
        })
      );
      await Promise.all(promises);
      toast.dismiss(loadingToast);
      toast.success("Orçamento mensal configurado!");
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Falha ao configurar orçamentos.");
    }
  };

  return (
    <div className="font-inter w-full max-w-7xl mx-auto pb-20 relative">
      
      {/* Top Standard Header for normal pages (mimicking layout aesthetic) */}
      <div className="flex items-center justify-between mb-8 bg-white/50 border-b border-slate-200 pb-4 pt-2 -mt-6 mx-[-2rem] px-8 sticky top-0 z-20 backdrop-blur-md">
        <div className="flex items-center space-x-8">
           <h1 className="text-xl font-bold text-slate-900 font-manrope">Orçamentos</h1>
           <div className="flex space-x-6 text-sm font-semibold">
             <span className="py-4 -mb-4 text-[#4f46e5] border-b-2 border-[#4f46e5]">Metas e Planejamento</span>
           </div>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start gap-8 mb-12">
           <div className="max-w-xl">
             <h2 className="text-5xl font-bold font-manrope text-slate-900 mb-4 tracking-tight">Metas de Gasto Mensal</h2>
             <p className="text-slate-500 font-medium leading-relaxed text-base">
               Defina o teto para cada categoria de despesa do mês. O sistema irá avisar quando você estiver próximo de ultrapassar.
             </p>
           </div>
           
           {/* Date Picker Action Card */}
           <div className="w-full xl:w-[380px]">
              <div className="bg-white rounded-xl p-6 flex flex-col mb-0 relative shadow-sm border border-slate-200 h-full w-full">
                 <p className="text-[10px] font-bold tracking-widest text-[#4f46e5] uppercase mb-4">Competência</p>
                 <div className="w-full relative mb-6">
                   <button type="button" onClick={() => setIsPickerOpen(!isPickerOpen)} className="w-full flex justify-between items-center bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 font-bold hover:bg-slate-100 transition-colors">
                     <span>{format(selectedMonth, 'MMMM yyyy', { locale: ptBR })}</span>
                     <span className="material-symbols-outlined text-[20px] text-slate-500">calendar_month</span>
                   </button>
                   {isPickerOpen && (
                     <div className="absolute top-14 left-0 bg-white shadow-xl rounded-xl z-50 p-2 border border-slate-100">
                       <DayPicker mode="single" month={selectedMonth} onMonthChange={setSelectedMonth} onSelect={(date) => {if(date) setSelectedMonth(date); setIsPickerOpen(false);}} locale={ptBR} initialFocus />
                     </div>
                   )}
                 </div>
                 
                 <button onClick={handleSaveChanges} className="w-full bg-[#4f46e5] text-white py-3 px-4 rounded-xl font-bold font-manrope shadow-md shadow-indigo-600/30 hover:bg-[#4338ca] transition-all flex items-center justify-center space-x-2">
                    <span className="material-symbols-outlined text-[18px]">save</span>
                    <span>Salvar Orçamentos</span>
                 </button>
              </div>
           </div>
       </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mt-8">
         <div className="grid grid-cols-1 divide-y divide-slate-100">
             {isLoading ? (
                <div className="p-12 text-center text-slate-500 font-medium">Sincronizando orçamentos...</div>
             ) : budgets.length === 0 ? (
                <div className="p-12 text-center text-slate-500 font-medium">Nenhum limite para definir por enquanto. Crie categorias primeiro.</div>
             ) : budgets.map(budget => {
                const percentage = budget.budget_amount > 0 ? (budget.spent_amount / budget.budget_amount) * 100 : 0;
                // Cores do progresso adaptadas
                const progressColor = percentage > 100 ? 'bg-red-500' : percentage > 75 ? 'bg-amber-400' : 'bg-green-500';

                return (
                   <div key={budget.category_id} className="p-6 md:p-8 hover:bg-slate-50 transition-colors">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                         
                         {/* Text Block */}
                         <div className="flex-1">
                            <h3 className="font-bold text-slate-900 text-lg font-manrope tracking-tight mb-2 flex items-center space-x-2">
                               <span className="material-symbols-outlined text-slate-400">label</span>
                               <span>{budget.category_name}</span>
                            </h3>
                            <div className="flex space-x-4 text-sm font-medium">
                               <p className="text-slate-500">Utilizado: <span className="text-slate-800">R$ {formatCurrency(budget.spent_amount)}</span></p>
                               <span className="text-slate-300">|</span>
                               <p className="text-slate-500">Restante: <span className="font-bold text-green-600">{Math.max(0, budget.budget_amount - budget.spent_amount).toFixed(2)}</span></p>
                            </div>
                         </div>

                         {/* Input Block */}
                         <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">R$</span>
                            <input 
                               type="number" 
                               value={budget.budget_amount} 
                               onChange={(e) => handleBudgetChange(budget.category_id, e.target.value)}
                               placeholder="0.00"
                               className="bg-white border-2 border-slate-200 rounded-xl pl-10 pr-4 py-3 font-bold text-slate-900 w-full md:w-36 focus:border-[#4f46e5] focus:outline-none transition-colors"
                            />
                         </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-6 w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                         <div className={`h-2.5 rounded-full transition-all duration-500 ${progressColor}`} style={{ width: `${Math.min(percentage, 100)}%` }}></div>
                      </div>
                   </div>
                );
             })}
         </div>
      </div>
    </div>
  );
}

export default BudgetsPage;